// eslint-disable-next-line max-len
import {
  Component,
  OnInit,
  ViewChild,
  Output,
  EventEmitter,
  AfterViewInit,
  Input,
  NgZone,
  OnDestroy,
  HostBinding,
  Optional,
  Self,
} from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { MatFormFieldControl } from '@angular/material/form-field';
import { from, of, Subject, Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { EditorLoaderService } from '../../../channels/services/editor-loader.service';

const PlainTextToInlineHtml = (text): string => {
  return text
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\n+/g, '<br>')
    .replace(/^\s/, '&nbsp;')
    .replace(/\s$/, '&nbsp;')
    .replace(/\s\s/g, ' &nbsp;');
};

@Component({
  selector: 'app-line-editor, app-line-editor[matCKInput]',
  templateUrl: './line-editor.component.html',
  styleUrls: ['./line-editor.component.scss'],
  providers: [
    {
      provide: MatFormFieldControl,
      useExisting: LineEditorComponent,
    },
  ],
})
export class LineEditorComponent
  implements
    OnInit,
    AfterViewInit,
    OnDestroy,
    ControlValueAccessor,
    MatFormFieldControl<string>
{
  static nextId = 0;

  public editor;

  private isEditorSettingData: boolean;
  private cvaOnChange?: (data: string) => void;
  private cvaOnTouched?: () => void;

  @Input() useLink: boolean;
  @Input() useShiftEnter: boolean;
  @Input() matCKInput: any;

  @Output() public blur: EventEmitter<{ target }> = new EventEmitter<{
    target;
  }>();
  @Output() public change: EventEmitter<string> = new EventEmitter<string>();
  @Output() public focus: EventEmitter<{ target }> = new EventEmitter<{
    target;
  }>();

  @ViewChild('editor', { static: true }) editorElem;

  private editorConfig = {
    placeholder: undefined,
    removePlugins: [
      'BlockToolbar',
      'ClipEmbed',
      'Footnote',
      'Heading',
      'Link',
      'MediaEmbed',
      'ParagraphPlaceholder',
      'AutoEmbed',
    ],
    toolbar: [
      'bold',
      'italic',
      'link',
      '|',
      'code',
      'superscript',
      'subscript',
      'strikethrough',
      'mark',
      '|',
      'math',
      'inlineDropdown',
    ],
    link: {
      decorators: {
        openInNewTab: {
          mode: 'automatic',
          callback: (url): boolean => true,
          attributes: {
            target: '_blank',
          },
        },
      },
    },
  };

  public value: string;
  public stateChanges = new Subject<void>();
  public focused: boolean;
  public errorState = false;
  public controlType = 'line-editor';

  private _placeholder: string;
  private _required = false;
  private _disabled = false;

  constructor(
    private ngZone: NgZone,
    @Optional() @Self() public ngControl: NgControl,
    private editorLoaderService: EditorLoaderService
  ) {
    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }
  }

  @HostBinding() id = `mat-line-editor-${LineEditorComponent.nextId++}`;
  @HostBinding('attr.aria-describedby') describedBy = '';

  ngOnInit(): void {
    if (this.useLink) {
      this.editorConfig.removePlugins.splice(
        this.editorConfig.removePlugins.indexOf('Link'),
        1
      );
    } else {
      this.editorConfig.toolbar.splice(
        this.editorConfig.toolbar.indexOf('link'),
        1
      );
    }
  }

  ngAfterViewInit(): void {
    this.editorLoaderService
      .createEditor(this.editorElem.nativeElement, this.editorConfig)
      .subscribe((editor) => this.ready(editor));
  }

  ngOnDestroy(): void {
    this.stateChanges.complete();

    if (this.editor) {
      this.editor.destroy();
    }
  }

  get isReady(): boolean {
    return !!this.editor;
  }

  @Input()
  get placeholder(): string {
    return this._placeholder;
  }
  set placeholder(placeholder: string) {
    if (this.matCKInput === undefined) {
      this.editorConfig.placeholder = placeholder;
    }

    this._placeholder = placeholder;
    this.stateChanges.next();
  }

  get shouldLabelFloat(): boolean {
    return this.focused || !this.empty;
  }

  get empty(): boolean {
    return !this.value;
  }

  @Input()
  get required(): boolean {
    return this._required;
  }
  set required(req: boolean) {
    this._required = !!req;
    this.stateChanges.next();
  }

  @Input()
  get disabled(): boolean {
    return this._disabled;
  }
  set disabled(dis: boolean) {
    this._disabled = !!dis;

    this._setEditorReadOnlyMode(!!dis);

    this.stateChanges.next();
  }

  ready(editor: any): void {
    this.editor = editor;

    if (this.value) {
      this.writeValue(this.value);
    }

    this._setEditorReadOnlyMode(this.disabled);

    editor.editing.view.document.on(
      'keydown',
      (evt, data) => {
        if (data.domEvent.key === 'Enter') {
          evt.stop();

          if (this.useShiftEnter) {
            editor.execute('shiftEnter');
          }
        }
      },
      { priority: 'highest' }
    );

    editor.editing.view.document.on(
      'clipboardInput',
      (evt, data) => {
        if (this.editor.isReadOnly) {
          return;
        }

        const dataTransfer = data.dataTransfer;

        const text = dataTransfer.getData('text/plain');

        const content = PlainTextToInlineHtml(text);

        data.content = editor.data.processor.toView(content);
      },
      { priority: 'highest' }
    );

    editor.model.document.on('change:data', (evt) => {
      if (this.isEditorSettingData) {
        return;
      }

      this.getData().subscribe((data) => {
        this.ngZone.run(() => {
          this.value = data;

          this.stateChanges.next();
          this.change.emit(this.value);

          if (this.cvaOnChange) {
            this.cvaOnChange(data);
          }
        });
      });
    });

    editor.editing.view.document.on('blur', (evt) => {
      this.ngZone.run(() => {
        if (this.cvaOnTouched) {
          this.cvaOnTouched();
        }

        this.focused = false;
        this.stateChanges.next();
        this.blur.emit({ target: this });
      });
    });

    editor.editing.view.document.on('focus', (evt) => {
      this.ngZone.run(() => {
        this.focused = true;
        this.stateChanges.next();
        this.focus.emit({ target: this });
      });
    });
  }

  writeValue(value: string | null): void {
    if (value == null) {
      value = '';
    }

    if (this.editor) {
      this.isEditorSettingData = true;
      this.setData(value).subscribe(() => {
        this.isEditorSettingData = false;
        this.stateChanges.next();
      });
    }

    this.value = value;
  }

  registerOnChange(fn: any): void {
    this.cvaOnChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.cvaOnTouched = fn;
  }

  setDescribedByIds(ids: string[]): void {
    this.describedBy = ids.join(' ');
  }

  onContainerClick(event: MouseEvent): void {
    this.editor.editing.view.focus();
  }

  private setData(data: string): Observable<void> {
    if (!this.editor) {
      throw new Error('editor not initialized yet');
    }

    return from(this.editor.setDataAsync(data)) as Observable<void>;
  }

  getData(): Observable<string> {
    if (!this.editor) {
      throw new Error('editor not initialized yet');
    }

    return from(this.editor.getDataAsync()).pipe(
      catchError((error) => of(this.editor.getData())),
      map((data: string) => data.substring(3, data.length - 4)) // remove p tag
    );
  }

  private _setEditorReadOnlyMode(readOnly: boolean): void {
    if (readOnly) {
      this.editor?.enableReadOnlyMode('LineEditorComponent');
    } else {
      this.editor?.disableReadOnlyMode('LineEditorComponent');
    }
  }
}
